import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import { useTheme, alpha } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DevicesIcon from '@mui/icons-material/Devices';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import VideocamIcon from '@mui/icons-material/Videocam';
import GroupIcon from '@mui/icons-material/Group';
import TimelineIcon from '@mui/icons-material/Timeline';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SensorsIcon from '@mui/icons-material/Sensors';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TuneIcon from '@mui/icons-material/Tune';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import ShieldIcon from '@mui/icons-material/Shield';
import { NavLink, useLocation } from 'react-router-dom';

import { useAuth, userHoldsExactRole } from '../auth/AuthContext';
import type { Role } from '../api/users';
import { BuildBadge } from './BuildBadge';
import { navInk, navAccent } from '../theme';

/**
 * Persistent sidebar (mdUp) + temporary drawer (mobile).
 *
 * Two zones, top → bottom:
 *   1. Work entries (NAV_GROUPS) — every day-to-day surface, rendered as
 *      one flat list. The NAV_GROUPS structure is retained only to order +
 *      role-gate the entries; the section headers were dropped once the
 *      tabbed-page migration collapsed most superadmin sections to a single
 *      item. An item renders only when its role gate matches, so one
 *      definition serves all three roles.
 *   2. Footer (FOOTER_NAV) — Settings / Profile / Help / About pinned
 *      at the foot of the rail so the utility entries sit apart from
 *      the work above and stay in the same place across every role.
 *
 * Every entry is role-gated by the same strict-role check the routes
 * use — sidebar visibility and route access stay in sync.
 */

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  /**
   * If set, only show when the user holds one of these exact roles.
   * A single role or an any-of array — operational entries both staff
   * roles reach use `['tenant_admin', 'coordinator']` (TENANT-VIEWER-ROLE).
   */
  requireRole?: Role | Role[];
}

interface NavGroup {
  /** Section header rendered above the group's items. */
  header: string;
  items: NavItem[];
}

// Grouped work entries. Role-gating means a group's membership differs
// per role (e.g. Overview = Dashboard + Tenants for a superadmin, but
// Dashboard + Cohort baseline for a tenant_admin); groups with no
// visible items for the current role are skipped, header and all.
const NAV_GROUPS: NavGroup[] = [
  {
    header: 'Overview',
    items: [
      // Dashboard is the post-login landing for all three roles; the
      // role-aware Dashboard component picks what to render.
      { to: '/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
      {
        to: '/admin/tenants',
        label: 'Tenants',
        icon: <BusinessIcon />,
        requireRole: 'superadmin',
      },
      {
        to: '/admin/licenses',
        label: 'Licenses',
        icon: <VpnKeyIcon />,
        requireRole: 'superadmin',
      },
    ],
  },
  {
    // Tenant-admin / coordinator work, in nav order. Procedures +
    // Procedure instances are merged into one Procedures page; Cohort
    // baseline, Tenant admins and Messages moved into Settings as tabs.
    header: 'Tenant',
    items: [
      {
        to: '/live-wall',
        label: 'Live',
        icon: <SensorsIcon />,
        // Participants see the cases they are recording in
        // (LIVE-WALL-PARTICIPANT); admins see the whole tenant.
        requireRole: ['tenant_admin', 'coordinator', 'participant'],
      },
      {
        to: '/tenant/analysis',
        label: 'Analysis',
        icon: <AnalyticsIcon />,
        requireRole: ['tenant_admin', 'coordinator', 'participant'],
      },
      {
        to: '/tenant/activity',
        label: 'Activities',
        icon: <TimelineIcon />,
        requireRole: ['tenant_admin', 'coordinator'],
      },
      {
        to: '/tenant/participants',
        label: 'Participants',
        icon: <GroupIcon />,
        requireRole: ['tenant_admin', 'coordinator'],
      },
      {
        to: '/tenant/procedures',
        label: 'Procedures',
        icon: <ListAltIcon />,
        requireRole: ['tenant_admin', 'coordinator'],
      },
      {
        to: '/tenant/devices',
        label: 'Devices',
        icon: <DevicesIcon />,
        requireRole: ['tenant_admin', 'coordinator'],
      },
      {
        // Directly after Devices: "a phone still holding a recording" is one
        // of the four things this reports, so the two are read together.
        to: '/tenant/data-integrity',
        label: 'Data integrity',
        icon: <FactCheckIcon />,
        requireRole: ['tenant_admin', 'coordinator'],
      },
      {
        to: '/tenant/appliances',
        label: 'Cameras',
        icon: <VideocamIcon />,
        requireRole: ['tenant_admin', 'coordinator'],
      },
      {
        to: '/tenant/organization',
        label: 'Departments & Facilities',
        icon: <BusinessIcon />,
        requireRole: 'tenant_admin',
      },
    ],
  },

  // ----- Superadmin (platform-ops) groups. Role-gated, so these are
  // invisible to tenant_admin / participant. Same grouped treatment as
  // the tenant work above: Tenancy overview → access → baselines →
  // config → operations → diagnostics, most-used first. -----
  {
    header: 'Access',
    items: [
      // Platform admins + access requests consolidated into one tabbed
      // Access page (old paths redirect to the matching tab).
      {
        to: '/admin/access',
        label: 'Access',
        icon: <ShieldIcon />,
        requireRole: 'superadmin',
      },
    ],
  },
  {
    header: 'Reference Library',
    items: [
      // The curated reference-value catalogue + submission review, in one
      // tabbed page (old /admin/baselines paths still resolve here).
      {
        to: '/admin/baselines',
        label: 'Library',
        icon: <EmojiEventsIcon />,
        requireRole: 'superadmin',
      },
    ],
  },
  {
    header: 'Configuration',
    items: [
      // Mail, MFA policy and AI / Insights are consolidated into one tabbed
      // Configuration page (the old paths redirect to the matching tab).
      {
        to: '/admin/configuration',
        label: 'Configuration',
        icon: <TuneIcon />,
        requireRole: 'superadmin',
      },
    ],
  },
  {
    header: 'Operations',
    items: [
      // System status, Workers and Capture-app releases are consolidated
      // into one tabbed Operations page (old paths redirect to the tab).
      {
        to: '/admin/operations',
        label: 'Operations',
        icon: <MonitorHeartIcon />,
        requireRole: 'superadmin',
      },
    ],
  },
  {
    header: 'Diagnostics',
    items: [
      // Crash reports, system logs and audit trail consolidated into one
      // tabbed Diagnostics page (old paths redirect to the matching tab).
      {
        to: '/admin/diagnostics',
        label: 'Diagnostics',
        icon: <ReportProblemIcon />,
        requireRole: 'superadmin',
      },
    ],
  },
];

// Pinned footer — utility surfaces that sit at the foot of the rail,
// apart from the work above, in the same place for every role.
const FOOTER_NAV: NavItem[] = [
  {
    to: '/tenant/settings',
    label: 'Settings',
    icon: <SettingsIcon />,
    requireRole: 'tenant_admin',
  },
  // Profile is reached via the topbar avatar menu for every role — no
  // sidebar entry (removed for participants per product).
  { to: '/help', label: 'Help', icon: <HelpOutlineIcon /> },
  // About: top-level entry, visible to every role — a stack snapshot +
  // team + copyright, useful for every audience.
  { to: '/about', label: 'About', icon: <InfoOutlinedIcon /> },
];


function visible(items: NavItem[], userObj: ReturnType<typeof useAuth>['user']) {
  // STRICT role check (userHoldsExactRole, not userHasRole) — route
  // guards want superadmin to wildcard onto every role, but the
  // sidebar should only surface entries that are actually relevant
  // to the operator's workflow. Otherwise the participant-only
  // Profile entry leaks into the platform-ops sidebar.
  return items.filter((n) => {
    if (!n.requireRole) return true;
    const roles = Array.isArray(n.requireRole) ? n.requireRole : [n.requireRole];
    return roles.some((r) => userHoldsExactRole(userObj, r));
  });
}

export function Sidebar({
  width,
  mobileOpen,
  onMobileClose,
}: {
  width: number;
  mobileOpen: boolean;
  onMobileClose(): void;
}) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const theme = useTheme();

  const footer = visible(FOOTER_NAV, user);

  // Version D "ink rail": a dark nav in both light and dark modes, with a
  // teal accent for the selected entry (an inset left bar + tint, no layout
  // shift). Icon-primary but labels always visible — the grouped, role-gated
  // structure below is untouched, so every role's nav keeps working.
  const mode = theme.palette.mode;
  const ink = navInk(mode);
  const accent = navAccent(mode);
  const idle = 'rgba(255,255,255,0.72)';
  const divColor = 'rgba(255,255,255,0.08)';

  // Icon-forward rail: the icon (larger) sits on top of a small, centred
  // label — the Version D mockup layout.
  const itemSx = {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: 0.75,
    mx: 1,
    my: 0.5,
    px: 0.5,
    py: 1.75,
    // Rounded "pill" rather than a full rectangle — the selected item reads
    // as a soft capsule floating in the rail, not an edge-to-edge block.
    borderRadius: '16px',
    color: idle,
    // Subtle hover motion — a small lift + colour wash (medical-software feel,
    // not a Bootstrap flat highlight).
    transition: 'background-color 140ms ease, transform 140ms ease',
    '& .MuiListItemIcon-root': {
      color: 'inherit',
      minWidth: 0,
      width: '100%',
      justifyContent: 'center',
      '& .MuiSvgIcon-root': { fontSize: 34 },
    },
    '& .MuiListItemText-root': { m: 0, flex: 'none', width: '100%' },
    '& .MuiListItemText-primary': {
      fontSize: 11.5,
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '0.01em',
      textAlign: 'center',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      wordBreak: 'break-word',
    },
    '&:hover': {
      bgcolor: alpha(accent, 0.14),
      transform: 'translateY(-1px)',
    },
    '&.Mui-selected': {
      // A brighter, more solid pill for the active item — clearly the
      // "you are here" element, with the accent icon on top.
      bgcolor: alpha(accent, 0.24),
      color: '#fff',
      boxShadow: `inset 0 0 0 1px ${alpha(accent, 0.35)}`,
      '&:hover': { bgcolor: alpha(accent, 0.28), transform: 'none' },
      '& .MuiListItemIcon-root': { color: accent },
      '& .MuiListItemText-primary': { fontWeight: 700 },
    },
  } as const;

  const renderItem = (item: NavItem) => {
    const active =
      pathname === item.to || pathname.startsWith(item.to + '/');
    return (
      <ListItemButton
        key={item.to}
        component={NavLink}
        to={item.to}
        onClick={onMobileClose}
        selected={active}
        sx={itemSx}
      >
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText primary={item.label} />
      </ListItemButton>
    );
  };

  const renderItems = (items: NavItem[]) => (
    <List dense>{items.map(renderItem)}</List>
  );

  // Work entries as one flat list — the section headers were dropped once
  // the tabbed-page migration collapsed most sections to a single item, so
  // the NAV_GROUPS structure now only orders + role-gates the entries.
  const renderGroups = (groups: NavGroup[]) => {
    const items = groups.flatMap((group) => visible(group.items, user));
    return items.length > 0 ? renderItems(items) : null;
  };

  const content = (
    // Vertical flex so the footer group pins to the bottom of the
    // drawer rather than sitting flush under the work groups.
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Spacer mirrors the TopBar toolbar so list items don't sit
          underneath it. */}
      <Toolbar />
      <Divider sx={{ borderColor: divColor }} />
      {renderGroups(NAV_GROUPS)}

      {/* Push the footer group to the bottom of the drawer. */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Settings / Profile / Help / About pinned at the foot, apart
          from the work above and in the same place for every role. */}
      {footer.length > 0 && (
        <>
          <Divider sx={{ borderColor: divColor }} />
          {renderItems(footer)}
        </>
      )}

      {/* Build provenance pinned at the very foot (ROADMAP #83a). */}
      <Divider sx={{ borderColor: divColor }} />
      <BuildBadge />
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { md: width }, flexShrink: { md: 0 } }}>
      {/* Mobile: temporary drawer triggered by the topbar hamburger */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width,
            bgcolor: ink,
            color: idle,
            borderRight: 'none',
          },
        }}
      >
        {content}
      </Drawer>

      {/* Desktop: permanent drawer alongside the main content */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width,
            bgcolor: ink,
            color: idle,
            borderRight: 'none',
          },
        }}
        open
      >
        {content}
      </Drawer>
    </Box>
  );
}
